import { all, call, put } from 'redux-saga/effects';
import { Formatters, Messages } from '../../../../codegen/Localize';
import { EncryptExtractErrorCode } from '../../../../ts/helpers/ErrorCodes';
import { DatasourceContentType, FlowContentType, WorkbookContentType } from '../../../../ts/models/ContentTypes';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import {
  IErrorItem, MultipleItemResult
} from '../../../../commonInfra/contentActions/MultipleItemResult';
import { Set } from '../../../../ts/models/Set';
import { DataSources } from '../../../../content/resources/Datasources';
import { Flows } from '../../../../ts/resources/Flows';
import { Workbooks } from '../../../../content/resources/Workbooks';
import { ContentDraftStatus } from '../../../../ts/ServerApi/types/ContentDraft';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { ContentActionNotification} from '../../../../commonInfra/contentActions/ContentActionNotification';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { EncryptContentAction, EncryptionActionType } from '../../../actions/spaces/EncryptContentAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { ServiceLocator } from '../../../ServiceLocator';
import { replaceDatasourceIdWithConnectionIdForMixedContent } from '../SpacesSagaHelpers';
import { reloadSpaceIfUpdatingCurrentSpace } from './RefreshContentSagas';
import { SagaIterator } from 'redux-saga';
import {
  createMultipleItemResultNotificationValues,
  MultipleItemResultNotificationValues
} from '../../../../commonInfra/contentActions/ActionMessages';

export function encryptDataSources(datasources: Set<IContentInfo>, encryptionActionType: EncryptionActionType): Promise<IResult> {
  if(datasources.size() < 1) {
    return null;
  }
  return DataSources.encryptDataSources(datasources.map(datasource => datasource.id), encryptionActionType);
}

export function encryptWorkbooks(workbooks: Set<IContentInfo>, encryptionActionType: EncryptionActionType): ng.IPromise<IResult> {
  if(workbooks.size() < 1) {
    return null;
  }
  return Workbooks.encryptWorkbooks(workbooks.map(workbook => workbook.id), encryptionActionType);
}

export function encryptFlows(flows: Set<IContentInfo>, encryptionActionType: EncryptionActionType): ng.IPromise<IResult> {
  if(flows.size() < 1) {
    return null;
  }

  return Flows.encryptFlows(flows.map(flow => flow.id), encryptionActionType);
}

export function encryptFlowDrafts(flows: Set<IContentInfo>, encryptionActionType: EncryptionActionType): ng.IPromise<IResult> {
  if(flows.size() < 1) {
    return null;
  }
  return Flows.encryptFlowDrafts(flows.map(flow => flow.id), encryptionActionType);
}

export function processResult(dataSourcesResult: IResult, workbooksResult: IResult, flowsResult: IResult): IResult {
  let result: IResult;
  const ignoredErrors = [EncryptExtractErrorCode.AlreadyDecrypting, EncryptExtractErrorCode.AlreadyEncrypted, EncryptExtractErrorCode.AlreadyEncrypting, EncryptExtractErrorCode.AlreadyUnencrypted];

  const errors = [];
  if(dataSourcesResult != null) {
    errors.push(...dataSourcesResult.errors);
  }

  if(workbooksResult != null) {
    errors.push(...workbooksResult.errors);
  }

  if(flowsResult != null){
    errors.push(...flowsResult.errors);
  }

  result = {
    errors: errors
  };

  //Remove errors returned by the API which we want to treat as success
  result.errors = result.errors.filter(error => !ignoredErrors.includes(error.code));
  return result;
}

export function showNotification(result: IResult, content: IContentInfo[], encryptionActionType: EncryptionActionType) {
  const notification: MultipleItemResultNotificationValues = createMultipleItemResultNotificationValues(
    new MultipleItemResult(Set.fromIdItemArray(content), result),
    {
      describeSuccessSummary: (successCount: number) => {
        return getSuccessSummary(successCount, encryptionActionType);
      },
      describeErrorSummary: (errorCount: number) => {
        return getErrorSummary(errorCount, encryptionActionType);
      },
      describeErrorDetail: (errorItem: IErrorItem<IContentInfo>) => {
        return {
          itemName: errorItem.item.name,
          errorMessage: getErrorMessageFor(errorItem.error.code)
        };
      }
    }
  );
  ContentActionNotification.displayItemizedToastsForResult(notification);
}

export function getSuccessSummary(successCount: number, encryptionActionType: EncryptionActionType): string {
  if(encryptionActionType === EncryptionActionType.encryptExtracts) {
    return Messages.encryptExtractSuccess({ITEM_COUNT: successCount, ITEM_COUNT_LOC: Formatters.formatCount(successCount)});
  } else {
    return Messages.decryptExtractSuccess({ITEM_COUNT: successCount, ITEM_COUNT_LOC: Formatters.formatCount(successCount)});
  }
}

export function getErrorSummary(errorCount: number, encryptionActionType: EncryptionActionType): string {
  if(encryptionActionType === EncryptionActionType.encryptExtracts) {
    return Messages.encryptExtractError({ERROR_COUNT: errorCount, ERROR_COUNT_LOC: Formatters.formatCount(errorCount)});
  } else {
    return Messages.decryptExtractError({ERROR_COUNT: errorCount, ERROR_COUNT_LOC: Formatters.formatCount(errorCount)});
  }
}

export function getErrorMessageFor(errorCode: number): string {
  switch (errorCode) {
    case EncryptExtractErrorCode.PermissionDenied:
      return Messages.permission_denied();
    case EncryptExtractErrorCode.NoEcryptableExtracts:
      return Messages.noEncryptableExtracts();
    default:
      return Messages.errorUnknown();
  }
}

export function* processEncryptContentAction(action: EncryptContentAction): SagaIterator {
  try {
    const mutableContentCopy: IContentInfo[] = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, action.contentArray);
    const datasources = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === DatasourceContentType.name));
    const workbooks = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === WorkbookContentType.name));
    const flows = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === FlowContentType.name));
    const actualFlows = flows.filter(flow => {
      return flow.draftStatus !== ContentDraftStatus.unpublished;
    });
    const flowDrafts = flows.filter(flow => {
      return flow.draftStatus === ContentDraftStatus.unpublished;
    });
    const [dataSourcesResult, workbooksResult, flowsResult] = (yield all([
      call(encryptDataSources, datasources, action.encryptionActionType),
      call(encryptWorkbooks, workbooks, action.encryptionActionType),
      call(encryptFlows, actualFlows, action.encryptionActionType),
      call(encryptFlowDrafts, flowDrafts, action.encryptionActionType),
    ])) as any; // TFS 1116084 - need to upgrade redux-saga in order to remove 'as any'

    const result = processResult(dataSourcesResult, workbooksResult, flowsResult);
    yield call(showNotification, result, mutableContentCopy, action.encryptionActionType);

    const didReload = yield call(reloadSpaceIfUpdatingCurrentSpace, action.contentArray);
    if (!didReload) {
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}
